import { Controller } from "@hotwired/stimulus";
import { formatDate, getAllJSON, getJSON, listResults } from "../api.js";

// Panel leadów personelu. NAJWAŻNIEJSZY niezmiennik: PII NIE jest renderowane masowo.
// Lista pokazuje wyłącznie 0-PII (id, rodzaj, region, typ) — nawet gdy API zwraca pełne PII
// uprawnionemu personelowi. Dane kontaktowe ujawnia wyłącznie jawna akcja „Pokaż dane"
// (retrieve pojedynczego leada → audyt lead.pii_viewed po stronie backendu). Dodawanie leada
// jest na OSOBNYM widoku (/management/leady/nowy/, kontroler `lead-wizard`) — ta strona ma
// tylko przycisk-link; świeżo dodany lead pojawia się przy zwykłym wejściu na listę.
export default class extends Controller {
  static targets = [
    "rows",
    "status",
    "banner",
    "modal",
    "modalBody",
    "modalError",
    "search",
    "kindFilter",
    "typeFilter",
    "voivFilter",
    "pagination",
    "pageInfo",
    "prevBtn",
    "nextBtn",
    "verticalFilter",
    "addedFrom",
    "addedTo",
  ];
  static values = {
    url: String, // /api/leads/
    voivodeshipsUrl: String, // /api/voivodeships/
    canViewPii: Boolean, // core.view_lead_pii
    canAdd: Boolean, // core.add_lead
  };

  static KIND_LABEL = { firma: "Firma", osoba: "Osoba" };

  connect() {
    if (this.hasBannerTarget) this.bannerTarget.hidden = this.canViewPiiValue;
    this.page = 1; // aktualna strona paginacji (server-side, DRF PageNumberPagination)
    this.load();
    this.#loadVoivodeshipFilter();
  }

  // Zmiana search/filtra → zawsze wracamy na stronę 1 i przeładowujemy listę.
  applyFilters() {
    this.page = 1;
    this.load();
  }

  // Debounce na wpisywaniu w polu szukania (change/klik „Szukaj" idą przez applyFilters).
  searchInput() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.applyFilters(), 300);
  }

  prevPage() {
    if (this.page > 1) {
      this.page -= 1;
      this.load();
    }
  }

  nextPage() {
    if (this.hasNextPage) {
      this.page += 1;
      this.load();
    }
  }

  async load() {
    let data;
    try {
      data = await getJSON(this.#listUrl());
    } catch {
      this.#status("Nie udało się załadować leadów. Odśwież stronę.");
      return;
    }
    const leads = listResults(data);
    this.rowsTarget.textContent = "";
    this.#renderPagination(data);
    if (!leads.length) {
      this.#status("Brak leadów.");
      return;
    }
    this.#status(null);
    const template = document.getElementById("lead-admin-row-template");
    for (const lead of leads) {
      const row = template.content.firstElementChild.cloneNode(true);
      // ŚWIADOMIE renderujemy TYLKO pola 0-PII — nigdy name/nip/phone/email/city,
      // nawet jeśli backend je zwrócił uprawnionemu personelowi.
      row.querySelector('[data-field="id"]').textContent = `#${lead.id}`;
      row.querySelector('[data-field="kind"]').textContent =
        this.constructor.KIND_LABEL[lead.kind] ?? lead.kind;
      // Nazwy z serializera staffu; identyfikatory tylko awaryjnie, gdy API ich nie poda
      // (nikt nie czyta „woj. 15 / pow. 8").
      const woj = lead.voivodeship_name || lead.voivodeship;
      const pow = lead.county_name || lead.county;
      row.querySelector('[data-field="region"]').textContent =
        `woj. ${woj} / pow. ${pow}`;
      row.querySelector('[data-field="type"]').textContent = lead.lead_type;

      const verticalCell = row.querySelector('[data-field="vertical"]');
      if (verticalCell) {
        verticalCell.textContent = lead.vertical_name || lead.vertical_slug || "—";
      }
      const addedCell = row.querySelector('[data-field="added"]');
      if (addedCell) {
        addedCell.textContent = lead.acquired_at ? formatDate(lead.acquired_at) : "—";
      }

      const reveal = row.querySelector('[data-field="reveal"]');
      if (reveal) {
        if (!this.canViewPiiValue) {
          reveal.hidden = true; // brak uprawnienia = brak przycisku ujawniania
        } else {
          reveal.hidden = false;
          reveal.setAttribute("data-action", "lead-admin#reveal");
          reveal.setAttribute("data-lead-admin-id-param", lead.id);
        }
      }
      this.rowsTarget.appendChild(row);
    }
  }

  // Jawne ujawnienie PII jednego leada = retrieve (backend audytuje lead.pii_viewed).
  async reveal(event) {
    const id = event.params.id;
    if (this.hasModalErrorTarget) this.modalErrorTarget.hidden = true;
    if (this.hasModalBodyTarget)
      this.modalBodyTarget.textContent = "Wczytywanie danych…";
    if (this.hasModalTarget) this.modalTarget.hidden = false;
    let lead;
    try {
      lead = await getJSON(`${this.urlValue}${id}/`);
    } catch {
      this.#modalError("Nie udało się pobrać danych leada.");
      return;
    }
    this.#renderPii(lead);
  }

  closeModal() {
    if (this.hasModalTarget) this.modalTarget.hidden = true;
  }

  stop(event) {
    event.stopPropagation();
  }

  // --- prywatne ---

  #renderPii(lead) {
    if (!this.hasModalBodyTarget) return;
    this.modalBodyTarget.textContent = "";
    const rows = [
      ["Nazwa", lead.name],
      ["NIP", lead.nip || "—"],
      ["Telefon", lead.phone],
      ["E-mail", lead.email],
      ["Miasto", lead.city_text || "—"],
      ["Źródło", lead.acquisition_source],
      ["Pozyskano", formatDate(lead.acquired_at)],
    ];
    for (const [label, value] of rows) {
      const line = document.createElement("div");
      line.style.cssText =
        "display:flex;justify-content:space-between;gap:16px;padding:6px 0;border-bottom:1px solid var(--color-ls-line-2);font-size:14px;";
      const key = document.createElement("span");
      key.style.color = "var(--color-ls-muted)";
      key.textContent = label;
      const val = document.createElement("span");
      val.className = "ls-mono";
      val.textContent = value ?? "—";
      line.append(key, val);
      this.modalBodyTarget.appendChild(line);
    }
  }

  // Buduje URL listy z bieżącym stanem paginacji i filtrów:
  // /api/leads/?page=N&search=…&kind=…&lead_type=…&voivodeship=…
  #listUrl() {
    const query = new URLSearchParams();
    if (this.page > 1) query.set("page", this.page);
    const search = this.hasSearchTarget ? this.searchTarget.value.trim() : "";
    if (search) query.set("search", search);
    const kind = this.hasKindFilterTarget ? this.kindFilterTarget.value : "";
    if (kind) query.set("kind", kind);
    const leadType = this.hasTypeFilterTarget ? this.typeFilterTarget.value : "";
    if (leadType) query.set("lead_type", leadType);
    const voivodeship = this.hasVoivFilterTarget
      ? this.voivFilterTarget.value
      : "";
    if (voivodeship) query.set("voivodeship", voivodeship);
    const vertical = this.hasVerticalFilterTarget ? this.verticalFilterTarget.value : "";
    if (vertical) query.set("vertical", vertical);
    const addedFrom = this.hasAddedFromTarget ? this.addedFromTarget.value : "";
    if (addedFrom) query.set("added_from", addedFrom);
    const addedTo = this.hasAddedToTarget ? this.addedToTarget.value : "";
    if (addedTo) query.set("added_to", addedTo);
    const text = query.toString();
    return text ? `${this.urlValue}?${text}` : this.urlValue;
  }

  // Info „strona X (Y leadów)" + przyciski ‹ Poprzednia / Następna › na bazie count/next/previous.
  #renderPagination(data) {
    this.hasNextPage = Boolean(data.next);
    const hasPrev = Boolean(data.previous);
    if (this.hasPageInfoTarget) {
      const count = data.count ?? listResults(data).length;
      this.pageInfoTarget.textContent = `strona ${this.page} (${count} leadów)`;
    }
    if (this.hasPrevBtnTarget) this.prevBtnTarget.disabled = !hasPrev;
    if (this.hasNextBtnTarget) this.nextBtnTarget.disabled = !this.hasNextPage;
    if (this.hasPaginationTarget) {
      // Ukryj pasek stron, gdy jest tylko jedna strona wyników.
      this.paginationTarget.hidden = !(this.hasNextPage || hasPrev);
    }
  }

  // Wypełnia dropdown filtra „Województwo: wszystkie" — jedyny konsument
  // voivodeshipsUrlValue od czasu przeniesienia formularza dodawania do lead-wizard.
  async #loadVoivodeshipFilter() {
    if (!this.hasVoivFilterTarget) return;
    let voivodeships;
    try {
      voivodeships = await getAllJSON(this.voivodeshipsUrlValue);
    } catch {
      return;
    }
    for (const voivodeship of voivodeships) {
      const option = document.createElement("option");
      option.value = voivodeship.id;
      option.textContent = voivodeship.name;
      this.voivFilterTarget.appendChild(option);
    }
  }

  #modalError(message) {
    if (this.hasModalBodyTarget) this.modalBodyTarget.textContent = "";
    if (!this.hasModalErrorTarget) return;
    this.modalErrorTarget.hidden = false;
    this.modalErrorTarget.textContent = message;
  }

  #status(message) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.hidden = message === null;
    if (message !== null) this.statusTarget.textContent = message;
  }
}
